// frontend/src/widgets/UserFormWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-16
// Updated:      2026-09-09 15:43 BST
//
// Description:
//   User form widget for creating/updating user profiles with avatar upload and role selection.
//
// Mobile Responsiveness (2026-09-09):
//   - Desktop retains the existing two-column form layout.
//   - Mobile collapses all fields to a single column.
//   - Inputs, select, file control and Save User become touch-safe on mobile.
//   - Avatar upload/preview remains full-width and the preview cannot overflow the widget.
//   - Save User spans the full form width on both mobile and desktop.
//   - Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary root sizing;
//       measure the complete natural form content;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the complete form so avatar preview appearance/removal
//     or responsive wrapping cannot leave the widget clipped.
//   - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//   - Email/password confirmation validation is unchanged.
//   - Multipart FormData payload and POST /users/ behaviour are unchanged.
//   - Existing role values, avatar upload and success/error handling are unchanged.
//   - Removed the unused useTheme hook/import; it had no effect on this widget's
//     rendered styling. Existing font-body styling remains unchanged.

import React, { useLayoutEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

export default function UserFormWidget() {
  const widgetRootRef = useRef(null);
  const formRef = useRef(null);

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [form, setForm] = useState({
    username: "",
    email: "",
    confirmEmail: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    role: "",
    avatar: null,
  });
  const [submitting, setSubmitting] = useState(false);

  const roles = [
    "Super Admin",
    "System Admin",
    "System Manager",
    "Operator",
    "User",
    "Client",
    "Guest",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      setForm((prev) => ({
        ...prev,
        avatar: file,
      }));

      const reader = new FileReader();
      reader.onload = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.email !== form.confirmEmail) {
      alert("Email addresses do not match.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      alert("Passwords do not match.");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("username", form.username);
      formData.append("email", form.email);
      formData.append("first_name", form.firstName);
      formData.append("last_name", form.lastName);
      formData.append("password", form.password);
      formData.append("role", form.role);

      if (form.avatar) {
        formData.append("avatar", form.avatar);
      }

      console.log("Creating user:", {
        username: form.username,
        email: form.email,
        role: form.role,
      });

      const response = await axiosInstance.post("/users/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      alert(`User ${response.data.email} created successfully!`);

      setForm({
        username: "",
        email: "",
        confirmEmail: "",
        firstName: "",
        lastName: "",
        password: "",
        confirmPassword: "",
        role: "",
        avatar: null,
      });

      setAvatarPreview(null);
    } catch (error) {
      console.error("Error creating user:", error);

      if (
        error.response?.status === 403 ||
        error.response?.status === 401
      ) {
        alert("You do not have permission to create a user.");
      } else {
        alert(
          error.response?.data?.detail ||
            "An error occurred while creating the user."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const formElement = formRef.current;

    if (!root || !formElement) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (
        node &&
        node !== document.body &&
        depth < 6
      ) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(
          node.style?.height || ""
        ).trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (
          widthMatches &&
          hasFixedInlineHeight
        ) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length
        ? candidates[0]
        : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const naturalFormHeight = Math.ceil(
          Math.max(
            formElement.getBoundingClientRect().height || 0,
            formElement.scrollHeight || 0
          )
        );

        root.style.height = "auto";
        root.style.minHeight =
          Number.isFinite(naturalFormHeight) &&
          naturalFormHeight > 0
            ? `${naturalFormHeight}px`
            : "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect =
            host.getBoundingClientRect();
          const rootRect =
            root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight +
                rootRect.height +
                8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height =
            `${requiredHostHeight}px`;
          host.style.minHeight =
            `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(
            () => applyMobileSizing()
          )
        : null;

    resizeObserver?.observe(formElement);

    const handleMediaChange = () =>
      applyMobileSizing();

    mediaQuery.addEventListener?.(
      "change",
      handleMediaChange
    );

    window.addEventListener(
      "resize",
      applyMobileSizing
    );

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.(
        "change",
        handleMediaChange
      );

      window.removeEventListener(
        "resize",
        applyMobileSizing
      );

      restoreHost();

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    avatarPreview,
    submitting,
  ]);

  const controlClass =
    "min-h-[44px] w-full min-w-0 rounded border border-gray-300 p-2 md:min-h-0";

  return (
    <section
      ref={widgetRootRef}
      className="font-body h-auto min-h-0 w-full overflow-visible rounded-lg bg-backgroundMode shadow-md md:h-full md:overflow-hidden"
    >
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6"
        style={{
          alignItems: "flex-start",
        }}
      >
        <input
          type="text"
          name="username"
          value={form.username}
          onChange={handleChange}
          placeholder="Username"
          className={controlClass}
          required
        />

        <input
          type="text"
          name="firstName"
          value={form.firstName}
          onChange={handleChange}
          placeholder="First Name"
          className={controlClass}
        />

        <input
          type="text"
          name="lastName"
          value={form.lastName}
          onChange={handleChange}
          placeholder="Last Name"
          className={controlClass}
        />

        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email Address"
          className={controlClass}
          required
        />

        <input
          type="email"
          name="confirmEmail"
          value={form.confirmEmail}
          onChange={handleChange}
          placeholder="Confirm Email Address"
          className={controlClass}
          required
        />

        <input
          type="password"
          name="password"
          value={form.password}
          onChange={handleChange}
          placeholder="Password"
          className={controlClass}
          required
        />

        <input
          type="password"
          name="confirmPassword"
          value={form.confirmPassword}
          onChange={handleChange}
          placeholder="Confirm Password"
          className={controlClass}
          required
        />

        <select
          name="role"
          value={form.role}
          onChange={handleChange}
          className={controlClass}
          required
        >
          <option
            value=""
            disabled
          >
            Select Role
          </option>

          {roles.map((role) => (
            <option
              key={role}
              value={role}
            >
              {role}
            </option>
          ))}
        </select>

        <div className="flex min-w-0 flex-col items-start md:col-span-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="min-h-[44px] w-full min-w-0 max-w-full text-sm file:mr-3 file:min-h-[40px] file:rounded file:border-0 file:px-3 file:py-2 file:font-semibold md:min-h-0"
          />

          {avatarPreview && (
            <img
              src={avatarPreview}
              alt="Avatar Preview"
              className="mt-2 h-24 w-24 max-w-full rounded-full border border-gray-300 object-cover"
            />
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="
            min-h-[44px]
            w-full
            md:col-span-2
            bg-btnActionNormal
            hover:bg-btnActionHover
            active:bg-btnActionClick
            text-white
            font-body
            font-semibold
            py-2 px-4
            rounded
            shadow
            transition-colors duration-200 ease-in-out
          "
          style={{
            cursor: submitting
              ? "not-allowed"
              : "pointer",
          }}
        >
          {submitting
            ? "Saving..."
            : "Save User"}
        </button>
      </form>
    </section>
  );
}
